/**
 * <store-availability> — worker-backed PDP store availability widget.
 *
 * Store rows (name / address / phone / C&C flag) are rendered SSR from the
 * `stores` metaobject. This element fetches live per-location STATUS from the
 * Cloudflare worker (data-worker-url) keyed by each row's data-location-id, then
 * patches the summary indicator + each store row on load and on variant change.
 *
 * Worker response shape:
 *   { variant_id, updated_at, levels: { "<numericLocationId>": { status } } }
 *   status ∈ "in_stock" | "low_stock" | "out_of_stock"
 *   A location absent from `levels` = out of stock at that store.
 *
 * Graceful degradation: no worker URL or a failed/aborted fetch never blanks the
 * widget. The SSR store list stays visible; rows fall back to a quiet "Check in
 * store" state and the last-known statuses are kept rather than wiped.
 *
 * ModalSizeSelector — in-modal size picker that forwards selections to the main
 * PDP variant-picker so the existing on:variant:change pipeline drives store
 * stock updates. Two-way sync: listens to on:variant:change to keep active button
 * + sold-out states in sync with PDP selections.
 */

/**
 * Handles the in-modal size selector UI for the store availability modal.
 * Reads product data embedded as JSON, renders size buttons, and forwards
 * taps to the main PDP variant picker (radio inputs) so the theme's
 * on:variant:change pipeline drives the worker re-fetch.
 */
class ModalSizeSelector {
  /**
   * @param {HTMLElement} container  .js-modal-size-selector placeholder div
   * @param {HTMLElement} productEl  Closest .js-product element (event bus)
   */
  constructor(container, productEl) {
    this.container = container;
    this.productEl = productEl;
    this.sizeOptionIndex = -1;
    this.sizeOptionName = '';
    this.allVariants = [];
    this.currentSelectedOptions = []; // mirrors the PDP's selected options

    // Read product data embedded by Liquid.
    const dataEl = container.closest('.modal__window')
      ? container.closest('.modal__window').querySelector('.js-store-size-data')
      : document.querySelector('.js-store-size-data');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this.sizeOptionIndex = data.sizeOptionIndex;
    this.sizeOptionName = data.sizeOptionName || '';
    this.allVariants = data.variants || [];

    // Guard: no real size option → leave container hidden.
    if (this.sizeOptionIndex < 0 || this.allVariants.length === 0) return;

    // Unique size values in declaration order.
    const seen = new Set();
    this.sizeValues = this.allVariants.reduce((acc, v) => {
      const val = v.options[this.sizeOptionIndex];
      if (val !== undefined && !seen.has(val)) {
        seen.add(val);
        acc.push(val);
      }
      return acc;
    }, []);

    // Only show the selector when there is more than one distinct size.
    if (this.sizeValues.length <= 1) return;

    this._render();
    this._syncFromPDP();

    // Keep in sync when variant changes anywhere on the page.
    // Store the reference so it can be removed on teardown and won't accumulate
    // if the section re-renders and re-instantiates ModalSizeSelector.
    if (this.productEl) {
      this._variantChangeHandler = (evt) => {
        const { variant, selectedOptions } = evt.detail || {};
        if (selectedOptions) this.currentSelectedOptions = selectedOptions;
        if (variant) this._updateAvailability();
        this._syncActiveFromOptions(this.currentSelectedOptions);
      };
      this.productEl.addEventListener('on:variant:change', this._variantChangeHandler);
    }
  }

  /** Remove the variant-change listener — call on element teardown. */
  destroy() {
    if (this.productEl && this._variantChangeHandler) {
      this.productEl.removeEventListener('on:variant:change', this._variantChangeHandler);
      this._variantChangeHandler = null;
    }
  }

  /** Render the size button row and unhide the container. */
  _render() {
    this.container.innerHTML = '';
    this.sizeValues.forEach((val) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'store-availability-modal__size-btn';
      btn.setAttribute('aria-pressed', 'false');
      btn.dataset.sizeValue = val;

      const span = document.createElement('span');
      span.textContent = val;
      btn.appendChild(span);

      btn.addEventListener('click', () => this._onSizeClick(val));
      this.container.appendChild(btn);
    });

    // Reveal after DOM is ready; _syncFromPDP() runs next and sets correct
    // active + availability state before any paint.
    this.container.removeAttribute('hidden');
  }

  /**
   * Forward a size tap to the main PDP variant picker.
   * Handles both selector types the theme supports:
   *   listed  → find the matching .js-option radio, set checked + dispatch 'change'
   *   dropdown → find the matching .custom-select__option, call custom-select.selectOption()
   * Either path bubbles into VariantPicker.handleVariantChange which fires on:variant:change.
   */
  _onSizeClick(sizeValue) {
    if (!this.productEl) return;

    // Locate the Size option selector element. data-option matches the option name
    // exactly (case-sensitive), so compare case-insensitively.
    const allSelectors = Array.from(this.productEl.querySelectorAll('.option-selector'));
    let sizeSelector = allSelectors.find(
      (el) => el.dataset.option && el.dataset.option.toLowerCase() === this.sizeOptionName.toLowerCase()
    );
    // Index fallback if the name lookup fails (e.g. custom option name).
    if (!sizeSelector) sizeSelector = allSelectors[this.sizeOptionIndex] || null;
    if (!sizeSelector) return;

    if (sizeSelector.dataset.selectorType === 'dropdown') {
      // Dropdown path — matches variant-picker.js applySearchParams() exactly.
      const optionEl = sizeSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(sizeValue)}"]`);
      if (!optionEl) return;
      const customSelect = optionEl.closest('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(optionEl);
      }
    } else {
      // Listed (radio) path.
      const radio = Array.from(sizeSelector.querySelectorAll('.js-option')).find(
        (input) => input.value === sizeValue
      );
      if (!radio) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Read the current selection from the PDP option selectors and sync state.
   * Handles both listed (radio) and dropdown (custom-select) selector types.
   * Called on mount — runs before _updateAvailability so init state is accurate.
   */
  _syncFromPDP() {
    if (!this.productEl) return;
    const options = [];
    this.productEl.querySelectorAll('.option-selector').forEach((fs) => {
      if (fs.dataset.selectorType === 'dropdown') {
        const selected = fs.querySelector('.custom-select__option[aria-selected="true"]');
        options.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
      } else {
        const checked = fs.querySelector('.js-option:checked');
        options.push(checked ? checked.value : null);
      }
    });
    this.currentSelectedOptions = options;
    this._syncActiveFromOptions(options);
    this._updateAvailability();
  }

  /** Set aria-pressed on buttons based on the current size option value. */
  _syncActiveFromOptions(selectedOptions) {
    const activeSizeValue = selectedOptions[this.sizeOptionIndex] || null;
    this.container.querySelectorAll('.store-availability-modal__size-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.sizeValue === activeSizeValue ? 'true' : 'false');
    });
  }

  /**
   * Update disabled/unavailable state of each size button based on which
   * variants are available given the current non-size options (e.g. colour).
   * We do NOT hide sold-out sizes — they stay tappable (matching PDP behaviour)
   * but show with a struck-through label and muted colour.
   */
  _updateAvailability() {
    const nonSizeOptions = [...this.currentSelectedOptions];
    // Blank out the size position so we can check availability across sizes.
    nonSizeOptions[this.sizeOptionIndex] = null;

    this.container.querySelectorAll('.store-availability-modal__size-btn').forEach((btn) => {
      const sizeVal = btn.dataset.sizeValue;
      const testOptions = [...nonSizeOptions];
      testOptions[this.sizeOptionIndex] = sizeVal;

      const variantForSize = this.allVariants.find((v) =>
        v.options.every((opt, i) => testOptions[i] === null || opt === testOptions[i])
      );

      const available = variantForSize ? variantForSize.available : false;
      if (available) {
        delete btn.dataset.unavailable;
      } else {
        btn.dataset.unavailable = '';
      }
    });
  }
}

/**
 * Single-open accordion for the store list, using the CSS grid-template-rows
 * 0fr↔1fr technique so each row animates to its EXACT content height — no
 * measuring, no overshoot, no pull-back jolt, and it handles the nested hours
 * mini-accordion growing the parent row mid-animation for free.
 *
 * Keeps the native <details>/<summary> markup (no-JS safe). Since the widget
 * carries `no-js-hidden`, JS is guaranteed: we intercept summary clicks and
 * drive open/close via a JS-toggled `.is-open` class on the <details> (that's
 * what animates), while keeping the native `open` attribute set whenever the
 * row is logically open so the content stays rendered through the close
 * animation and semantics/chevron stay correct. Only ONE row open at a time.
 *
 * Nested hours mini-accordion (.js-hours-acc) uses the same grid-rows technique
 * and is wired per store row.
 */
class ModalAccordion {
  /** @param {HTMLElement} root  Element containing the .js-store-row accordions. */
  constructor(root) {
    this.root = root;
    this.details = Array.from(root.querySelectorAll('.store-availability-drawer__accordion'));
    this._summaryHandler = this._onSummaryClick.bind(this);
    this._summaries = [];
    this._hoursToggles = [];
    this._hoursHandler = this._onHoursToggle.bind(this);

    this.details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      const body = detail.querySelector('.store-availability-drawer__accordion-body');
      if (!summary || !body) return;
      summary.addEventListener('click', this._summaryHandler);
      this._summaries.push(summary);
    });

    // Nested per-store opening-hours mini-accordion.
    Array.from(root.querySelectorAll('.js-hours-toggle')).forEach((toggle) => {
      toggle.addEventListener('click', this._hoursHandler);
      this._hoursToggles.push(toggle);
    });
  }

  _onSummaryClick(evt) {
    // Prevent the native <details> default toggle so our class drives the state.
    evt.preventDefault();
    const summary = evt.currentTarget;
    const detail = summary.closest('.store-availability-drawer__accordion');
    if (!detail) return;

    if (detail.classList.contains('is-open')) {
      this._close(detail);
    } else {
      // Close any other open row first — single-open behaviour.
      this.details.forEach((other) => {
        if (other !== detail && other.classList.contains('is-open')) this._close(other);
      });
      this._open(detail);
    }
  }

  _open(detail) {
    detail.open = true; // keep content rendered + semantics correct
    this._markAnimating(detail); // guard worker mutations from re-resolving 1fr mid-flight
    detail.classList.add('is-open'); // grid-template-rows: 0fr -> 1fr animates
    const summary = detail.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  _close(detail) {
    this._markAnimating(detail);
    detail.classList.remove('is-open'); // animate 1fr -> 0fr; content stays rendered
    const summary = detail.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    // Drop the native open attr once the close animation has finished, so the
    // chevron/semantics reset without yanking content mid-animation.
    this._afterTransition(detail.querySelector('.store-availability-drawer__accordion-body'), () => {
      if (!detail.classList.contains('is-open')) detail.open = false;
    });
  }

  /**
   * Flag a row as mid-animation so body-affecting mutations from the worker
   * (e.g. toggling the C&C badge) are deferred until the open/close transition
   * settles — otherwise the 1fr target re-resolves mid-flight and the row
   * grows-then-shrinks (the intermittent jolt). Cleared on transitionend.
   * StoreAvailability.setRow() reads `dataset.animating` and queues a flush via
   * the `animating-flush` event we dispatch here.
   */
  _markAnimating(detail) {
    const row = detail.closest('.js-store-row') || detail;
    row.dataset.animating = '';
    this._afterTransition(detail.querySelector('.store-availability-drawer__accordion-body'), () => {
      delete row.dataset.animating;
      row.dispatchEvent(new CustomEvent('animating-flush', { bubbles: true }));
    });
  }

  /** Toggle the nested opening-hours mini-accordion within a store row. */
  _onHoursToggle(evt) {
    const toggle = evt.currentTarget;
    const acc = toggle.closest('.js-hours-acc');
    if (!acc) return;
    const open = acc.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /** Run `cb` once on the next grid-template-rows transitionend (safety timeout). */
  _afterTransition(el, cb) {
    if (!el) {
      cb();
      return;
    }
    let done = false;
    const finish = (e) => {
      if (e && e.propertyName && e.propertyName !== 'grid-template-rows') return;
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', finish);
      cb();
    };
    el.addEventListener('transitionend', finish);
    setTimeout(finish, 400);
  }

  destroy() {
    this._summaries.forEach((s) => s.removeEventListener('click', this._summaryHandler));
    this._hoursToggles.forEach((t) => t.removeEventListener('click', this._hoursHandler));
    this._summaries = [];
    this._hoursToggles = [];
  }
}

if (!customElements.get('store-availability')) {
  class StoreAvailability extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map(); // variantId -> levels object
      this.lastLevels = undefined; // last successful levels, for fallback
      // Current variant's website (online) availability — combined with in-store
      // stock to drive the summary label. Updated on every variant change.
      this.onlineAvailable = this.dataset.onlineAvailable === 'true';
      this.openBtn = this.querySelector('.js-open-store-modal');

      // Move modal to <body> so it escapes any parent stacking contexts.
      this.modal = this.querySelector('.js-store-modal');
      if (this.modal) document.body.appendChild(this.modal);

      if (this.openBtn && this.modal) {
        this.openBtn.addEventListener('click', () => this.modal.open(this.openBtn));
      }

      this._productEl = this.closest('.js-product');
      if (this._productEl) {
        // Keep the bound ref so disconnectedCallback can remove the listener.
        this._onVariantChange = this.handleVariantChange.bind(this);
        this._productEl.addEventListener('on:variant:change', this._onVariantChange);
      }

      // In-modal size selector — initialised after modal is moved to body.
      const sizeContainer = this.modal
        ? this.modal.querySelector('.js-modal-size-selector')
        : this.querySelector('.js-modal-size-selector');
      if (sizeContainer) {
        this.sizeSelector = new ModalSizeSelector(sizeContainer, this.closest('.js-product'));
      }

      // Single-open accordion + Venroy-style height animation for the store rows.
      const accordionRoot = this.modal || this;
      this.accordion = new ModalAccordion(accordionRoot);
    }

    connectedCallback() {
      const variantId = this.dataset.variantId;
      if (!variantId) return;
      // De-synchronise the launch herd: defer the first fetch to idle time with a
      // small random delay so ~100k near-simultaneous PDP loads at sale-open don't
      // fire in lockstep. Keeps the fetch off the critical render path too.
      const kick = () => this.loadAvailability(variantId);
      const delay = 100 + Math.floor(Math.random() * 700);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => setTimeout(kick, delay), { timeout: 2500 });
      } else {
        setTimeout(kick, delay);
      }
    }

    disconnectedCallback() {
      if (this.sizeSelector) this.sizeSelector.destroy();
      if (this.accordion) this.accordion.destroy();
      if (this._productEl && this._onVariantChange) {
        this._productEl.removeEventListener('on:variant:change', this._onVariantChange);
        this._onVariantChange = null;
      }
      // Remove the body-relocated modal so it doesn't leak on section re-render.
      if (this.modal && this.modal.parentNode === document.body) this.modal.remove();
      if (this.fetchController) this.fetchController.abort();
    }

    get workerUrl() {
      const url = (this.dataset.workerUrl || '').replace(/\/+$/, '');
      return url || null;
    }

    handleVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant) return; // keep last-known UI rather than blanking on null variant
      this.dataset.variantId = variant.id;
      this.onlineAvailable = !!variant.available;
      this.loadAvailability(variant.id);
      this._syncModalVariantPills(variant);
    }

    /** Update the variant pill text in the modal when the PDP selection changes. */
    _syncModalVariantPills(variant) {
      if (!this.modal || !variant || !variant.options) return;
      const pills = this.modal.querySelectorAll('.js-modal-variant-pill');
      variant.options.forEach((optVal, i) => {
        if (pills[i]) pills[i].textContent = optVal;
      });
    }

    async loadAvailability(variantId) {
      if (!this.workerUrl) {
        // No worker configured — leave SSR rows, fall back to "check in store".
        this.applyLevels(null);
        return;
      }

      // L1: in-memory cache (this page view).
      if (this.cache.has(variantId)) {
        this.applyLevels(this.cache.get(variantId));
        return;
      }

      // L2: sessionStorage cache — carries across PDP navigations within the
      // session, so browsing many products doesn't re-hit the worker per page.
      const stored = this._sessionGet(variantId);
      if (stored) {
        this.cache.set(variantId, stored);
        this.lastLevels = stored;
        this.applyLevels(stored);
        return;
      }

      // Abort any in-flight request so rapid variant switches don't race —
      // the last selection always wins and we avoid stale renders/flicker.
      if (this.fetchController) this.fetchController.abort();
      this.fetchController = new AbortController();
      const timeout = setTimeout(() => this.fetchController.abort(), 8000);

      this.setSummary('loading');
      try {
        const res = await fetch(`${this.workerUrl}/v1/availability/${variantId}`, {
          headers: { Accept: 'application/json' },
          signal: this.fetchController.signal,
        });
        // Non-2xx (incl. the worker's 503 on a Shopify throttle/failure) → quiet
        // fallback, NEVER a false "out of stock". Keep last-known data if we have it.
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        // Belt-and-braces: if the worker ever signals failure in a 200 body, treat
        // it as unknown and DO NOT poison the cache with empty (= all-OOS) levels.
        if (data && data.error) {
          if (this.dataset.variantId === String(variantId)) {
            this.applyLevels(this.lastLevels !== undefined ? this.lastLevels : null);
          }
          return;
        }
        const levels = data && data.levels ? data.levels : {};
        this.cache.set(variantId, levels);
        this.lastLevels = levels;
        this._sessionSet(variantId, levels);
        // Ignore stale responses if the variant changed while in flight.
        if (this.dataset.variantId === String(variantId)) this.applyLevels(levels);
      } catch (e) {
        // Aborts (superseded variant change) are silent. For real failures or
        // timeouts, keep the last-known statuses if we have them — a stale-but-
        // useful state beats wiping every row to "out of stock".
        if (e.name === 'AbortError') return;
        this.applyLevels(this.lastLevels !== undefined ? this.lastLevels : null);
      } finally {
        clearTimeout(timeout);
      }
    }

    /** sessionStorage L2 cache — short-TTL, shared across PDP navigations. */
    _sessionGet(variantId) {
      try {
        const raw = sessionStorage.getItem('velo-sa:' + variantId);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (!o || (Date.now() - o.t) > 300000) return null; // 5-min freshness
        return o.l;
      } catch (e) {
        return null;
      }
    }

    _sessionSet(variantId, levels) {
      try {
        sessionStorage.setItem('velo-sa:' + variantId, JSON.stringify({ t: Date.now(), l: levels }));
      } catch (e) {
        /* quota / private mode — ignore; the in-memory cache still applies */
      }
    }

    /**
     * @param {Object|null} levels  Worker `levels` map, or null when unknown
     *                              (no worker / failure with no prior data).
     */
    applyLevels(levels) {
      // Query from the modal (moved to body) or fall back to self for safety.
      const root = this.modal || this;
      const rows = root.querySelectorAll('.js-store-row');
      let anyAvailable = false;

      rows.forEach((row) => {
        const locId = row.dataset.locationId;
        const status = levels ? (levels[locId] ? levels[locId].status : 'out_of_stock') : 'unknown';
        const available = status === 'in_stock' || status === 'low_stock';
        if (available) anyAvailable = true;
        this.setRow(row, status, available);
      });

      // levels === null → in-store stock is unknown (no worker / failure). Combine
      // the (un)known store state with the current online availability.
      this.setSummary({ storeKnown: levels !== null, anyAvailable });
    }

    setRow(row, status, available) {
      const indicator = row.querySelector('.js-store-indicator');
      const statusEl = row.querySelector('.js-store-status');
      const ccBadge = row.querySelector('.js-store-cc-badge');

      if (indicator) {
        indicator.classList.remove(
          'store-availability__indicator--loading',
          'store-availability__indicator--available',
          'store-availability__indicator--low',
          'store-availability__indicator--unavailable'
        );
        const cls =
          status === 'in_stock'
            ? 'store-availability__indicator--available'
            : status === 'low_stock'
            ? 'store-availability__indicator--low'
            : status === 'unknown'
            ? 'store-availability__indicator--loading'
            : 'store-availability__indicator--unavailable';
        indicator.classList.add(cls);
      }

      if (statusEl) {
        statusEl.classList.remove(
          'store-availability-drawer__status--in-stock',
          'store-availability-drawer__status--low-stock',
          'store-availability-drawer__status--out-of-stock'
        );
        let label;
        let cls;
        if (status === 'in_stock') {
          label = statusEl.dataset.labelInStock;
          cls = 'store-availability-drawer__status--in-stock';
        } else if (status === 'low_stock') {
          label = statusEl.dataset.labelLowStock;
          cls = 'store-availability-drawer__status--low-stock';
        } else if (status === 'unknown') {
          // Worker unreachable — quiet, non-alarming fallback.
          label = statusEl.dataset.labelChecking;
          cls = '';
        } else {
          label = statusEl.dataset.labelOutOfStock;
          cls = 'store-availability-drawer__status--out-of-stock';
        }
        statusEl.textContent = label || '';
        if (cls) statusEl.classList.add(cls);
      }

      // C&C badge lives INSIDE the animated body, so toggling it mid-transition
      // re-resolves the 1fr target and causes the grow-then-shrink jolt. If the
      // row is animating, defer this mutation until the transition settles
      // (flushed via the 'animating-flush' event); otherwise apply immediately.
      if (ccBadge) {
        const applyBadge = () => {
          ccBadge.hidden = !(row.hasAttribute('data-click-collect') && available);
        };
        if (row.dataset.animating !== undefined) {
          row.addEventListener('animating-flush', applyBadge, { once: true });
        } else {
          applyBadge();
        }
      }
    }

    /**
     * Drive the combined summary line (online + in-store).
     * @param {('loading'|{storeKnown:boolean, anyAvailable:boolean})} state
     *   'loading' → in-flight fetch, neutral checking state.
     *   Object    → resolved store state, combined with this.onlineAvailable per
     *               the matrix below.
     *
     *   | online | in-store        | label                | dot          |
     *   | true   | true (known)    | Available In-Store & Online | available |
     *   | true   | false (known)   | Available Online            | available |
     *   | false  | true (known)    | Available In-Store          | available |
     *   | false  | false (known)   | Currently Unavailable       | unavailable |
     *   | true   | unknown/loading | Available Online            | available |
     *   | false  | unknown/loading | (checking — loading)        | loading   |
     */
    setSummary(state) {
      const indicator = this.querySelector('.js-store-summary-indicator');
      const text = this.querySelector('.js-store-summary-text');
      const online = this.onlineAvailable;

      // Resolve to one of: 'available' | 'unavailable' | 'loading', plus a label key.
      let dot = 'loading';
      let labelAttr = 'labelLoading';

      if (state === 'loading') {
        // Fetch in flight. If online is known-available, show that immediately
        // (don't make an in-stock product look like it's "checking"); otherwise
        // stay neutral until the worker confirms in-store.
        if (online) {
          dot = 'available';
          labelAttr = 'labelOnline';
        } else {
          dot = 'loading';
          labelAttr = 'labelLoading';
        }
      } else {
        const storeKnown = state && state.storeKnown;
        const inStore = !!(state && state.anyAvailable);
        if (online && storeKnown && inStore) {
          dot = 'available';
          labelAttr = 'labelInstoreOnline';
        } else if (online && storeKnown && !inStore) {
          dot = 'available';
          labelAttr = 'labelOnline';
        } else if (!online && storeKnown && inStore) {
          dot = 'available';
          labelAttr = 'labelInstore';
        } else if (!online && storeKnown && !inStore) {
          dot = 'unavailable';
          labelAttr = 'labelUnavailableAll';
        } else if (online && !storeKnown) {
          // Worker unreachable but online is available — fail open to "Available
          // Online", never assert in-store without confirmed data.
          dot = 'available';
          labelAttr = 'labelOnline';
        } else {
          // !online && !storeKnown — stay neutral/checking.
          dot = 'loading';
          labelAttr = 'labelLoading';
        }
      }

      if (indicator) {
        indicator.classList.remove(
          'store-availability__indicator--loading',
          'store-availability__indicator--available',
          'store-availability__indicator--unavailable'
        );
        indicator.classList.add('store-availability__indicator--' + dot);
      }

      if (text) text.textContent = text.dataset[labelAttr] || '';
    }
  }

  customElements.define('store-availability', StoreAvailability);
}
